"use client";

import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { useCreatePageStore } from "@/stores/createPageStore";
import { useAuth } from "@/context/providers/AuthProvider";
import { useSafeCreditsContext } from "@/context/providers/CreditsProvider";
import { generatePresets } from "@/services/createAgentService.presets";
import { detectPhase } from "@/services/createAgentService.phase";

/**
 * Computes the presets shown in chat. Server-provided presets are
 * authoritative, including an intentionally empty action surface. Fallback
 * presets are synthesized (via detectPhase + generatePresets) only for a
 * restored design that has no assistant transcript to own the next decision.
 * `visiblePresets` additionally hides the preset-type CTA once a preset type
 * is chosen.
 */
export function useDisplayPresets() {
  const { user } = useAuth();
  const { balance } = useSafeCreditsContext();
  const agent = useCreatePageStore(useShallow(state => state.agent));
  const formData = useCreatePageStore(state => state.form.data);
  const imageUrl = useCreatePageStore(
    useShallow(state => state.generation.images.front.url)
  );
  const backImageUrl = useCreatePageStore(
    useShallow(state => state.generation.images.back.url)
  );
  const presets = useMemo(() => agent.presets || [], [agent.presets]);
  const presetsLoading = agent.conversation.presetsLoading;
  const hasAssistantTranscript = agent.conversation.messages.some(
    message => message.role === "assistant"
  );
  const hasPresetType = Boolean(formData.preset_type);
  const displayPresets = useMemo(() => {
    // When loading, show presets (skeleton handles empty)
    if (presetsLoading) {
      return presets;
    }

    // Server responses are authoritative. Only synthesize fallback presets
    // when the agent returned none, so the client does not splice stale CTAs
    // into a deliberate server-provided option set.
    if (presets.length > 0) {
      return presets;
    }

    // An assistant turn with no actions is deliberate: it may have asked one
    // focused question for the composer. Synthesizing readiness-based CTAs
    // here creates a second, unrelated decision (for example, a Generate
    // button underneath a refinement question). Restoration with no transcript
    // remains the only fallback case.
    if (hasAssistantTranscript) {
      return [];
    }

    const detectedPhase = detectPhase(formData, {
      hasFrontImage: Boolean(imageUrl),
      hasBackImage: Boolean(backImageUrl),
    });
    // Prefer detectedPhase over agent.phase for fallback - agent.phase can be stale
    // (e.g. session restore without form/images), causing empty presets when phase
    // suggests front_review/back_design but we have no image
    const phaseToUse =
      detectedPhase === "initial" && hasPresetType
        ? "front_design"
        : detectedPhase || agent.phase;
    // Include imageUrl and backImageUrl in the form data so generatePresets
    // can correctly determine hasFrontImage/hasBackImage for preset availability
    const creditAwareFormData = {
      ...formData,
      isLoggedIn: Boolean(user),
      ...(typeof balance === "number" ? { hasCredits: balance > 0 } : {}),
      // These are needed by generatePresets to determine preset availability
      ...(imageUrl ? { imageUrl } : {}),
      ...(backImageUrl ? { backImageUrl } : {}),
    };
    return generatePresets(phaseToUse, creditAwareFormData);
  }, [
    presets,
    presetsLoading,
    formData,
    imageUrl,
    backImageUrl,
    agent.phase,
    hasPresetType,
    hasAssistantTranscript,
    user,
    balance,
  ]);
  const visiblePresets = hasPresetType
    ? displayPresets.filter(preset => preset.actionId !== "set_preset_type")
    : displayPresets;

  return { displayPresets, visiblePresets };
}
